import type { ApiEnvelope } from "./api-envelope";
import { isApiSuccessCode } from "./api-envelope";
import { ApiError } from "./errors";

/** 判断 JSON 体是否像 Spring {@code ApiResponse} 信封 */
export function isApiEnvelopeShape(value: unknown): value is ApiEnvelope<unknown> {
  if (value == null || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return typeof record.code === "string" && "data" in record;
}

/** 从响应文本解析信封；非 JSON 或不像信封时返回 null */
export function parseApiEnvelopeJson(rawBody: string): ApiEnvelope<unknown> | null {
  const trimmed = rawBody.trim();
  if (!trimmed) return null;
  try {
    const value = JSON.parse(trimmed) as unknown;
    return isApiEnvelopeShape(value) ? value : null;
  } catch {
    return null;
  }
}

/** 将非 JSON 或过长原文压缩为可展示片段 */
export function summarizeRawBodySnippet(
  rawBody: string,
  maxLength = 240,
): string | undefined {
  const text = rawBody.trim().replace(/\s+/g, " ");
  if (!text) return undefined;
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

/** 解析 {@code Retry-After} 头（秒） */
export function parseRetryAfterHeaderValue(
  raw: string | null | undefined,
): number | undefined {
  if (raw == null || raw === "") return undefined;
  const seconds = Number.parseInt(String(raw).trim(), 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
}

type HeaderLike =
  | Headers
  | { get(name: string): string | null }
  | Record<string, string | number | undefined | null | unknown>;

/** 从 fetch {@link Headers} 或 Axios 风格头对象读取 Retry-After（秒） */
export function readRetryAfterSecondsFromHeaders(
  headers: HeaderLike | null | undefined,
): number | undefined {
  if (headers == null) return undefined;
  let raw: string | null | undefined;
  if (typeof (headers as { get?: unknown }).get === "function") {
    const h = headers as { get: (name: string) => string | null };
    raw = h.get("Retry-After") ?? h.get("retry-after");
  } else {
    const record = headers as Record<string, unknown>;
    const value = record["Retry-After"] ?? record["retry-after"];
    raw = value == null ? undefined : String(value);
  }
  return parseRetryAfterHeaderValue(raw);
}

/** 429 用户可见文案（含等待秒数） */
export function formatRateLimitUserMessage(
  fallback: string,
  retryAfterSeconds?: number,
): string {
  if (retryAfterSeconds == null) return fallback;
  return `${fallback}，请 ${retryAfterSeconds} 秒后再试`;
}

export type ApiHttpFailureInput = {
  status: number;
  rawBody?: string;
  parsed?: ApiEnvelope<unknown> | null;
  retryAfterSeconds?: number;
  /** 429 且无业务 message 时的默认文案 */
  rateLimitFallback?: string;
};

export type ApiHttpFailureParts = {
  message: string;
  code?: string;
  status: number;
  detail?: string;
  retryAfterSeconds?: number;
};

const HTTP_STATUS_USER_MESSAGES: Partial<Record<number, string>> = {
  400: "请求参数有误",
  401: "未登录或登录已失效",
  403: "无权访问该资源",
  404: "请求的资源不存在",
  408: "请求超时",
  500: "服务内部错误",
  502: "网关错误",
  503: "服务暂时不可用",
};

/** 根据 HTTP 状态、信封与原文生成失败字段（不抛错） */
export function resolveApiHttpFailure(
  input: ApiHttpFailureInput,
): ApiHttpFailureParts {
  const rawBody = input.rawBody ?? "";
  const parsed = input.parsed ?? null;
  const detail =
    parsed == null ? summarizeRawBodySnippet(rawBody) : undefined;
  const retryAfter = input.retryAfterSeconds;
  const statusMessage = HTTP_STATUS_USER_MESSAGES[input.status];

  const message =
    input.status === 429
      ? formatRateLimitUserMessage(
          parsed?.message ||
            input.rateLimitFallback ||
            "请求过于频繁",
          retryAfter,
        )
      : parsed?.message ||
        statusMessage ||
        detail ||
        (input.status > 0 ? `请求失败（${input.status}）` : "网络连接失败，请稍后重试");

  return {
    message,
    code: parsed?.code,
    status: input.status,
    detail,
    retryAfterSeconds: retryAfter,
  };
}

/** 构造并抛出 {@link ApiError}（信封业务失败或非 2xx） */
export function throwApiHttpFailure(input: ApiHttpFailureInput): never {
  const parts = resolveApiHttpFailure(input);
  throw new ApiError(parts.message, {
    code: parts.code,
    status: parts.status,
    detail: parts.detail,
  });
}

/**
 * 解包已解析的响应体（Axios `response.data` 或 fetch 解析结果）。
 * 非信封则原样返回；信封失败抛 {@link ApiError}。
 */
export function unwrapApiEnvelopeObject<T>(
  data: unknown,
  httpStatus: number,
  options?: Pick<ApiHttpFailureInput, "retryAfterSeconds" | "rateLimitFallback">,
): T {
  if (!isApiEnvelopeShape(data)) {
    return data as T;
  }
  if (isApiSuccessCode(data.code)) {
    return data.data as T;
  }
  throwApiHttpFailure({
    status: httpStatus,
    parsed: data,
    rawBody: "",
    ...options,
  });
}
